<template>
    <div>
        <MainModal 
            :show-modal="showModal"
            size="md" 
            title="Create check-in event" 
            @close="closeModal"
        >
            <template #body>
                <form @submit.prevent="">
                    <div class="client__modal__body scroll-height">
                        <div class="flex align-items-center mb--15 gap-2">
                            <div class="flex-1">
                                <FormLabel title="Check-in category" info-type="required" />
                                <CustomDropdown 
                                    id="checkInTypeDropdown" 
                                    v-model="checkInForm.type" 
                                    :options="checkInTypes"
                                    index-label="name"
                                    size="sm"
                                    :searchable="false"
                                    btn-classes="text--normal" 
                                    list-type="types" 
                                    @select="v => checkInForm.type = v" 
                                />
                            </div>
                            <div class="flex-1">
                                <FormLabel title="Scheduled time" />
                                <v-date-picker
                                    v-model="checkInForm.startDate" class="text--sm" 
                                    mode="dateTime" borderless :min-date="new Date()"
                                    is24hr title-position="left"
                                >
                                    <template #default="{ inputValue, inputEvents }">
                                        <input
                                            class="form-control form-control-sm text--sm"
                                            :value="inputValue"
                                            nav-visibility="click"
                                            v-on="inputEvents"
                                        >
                                    </template>
                                </v-date-picker>
                            </div>
                        </div>

                        <FormInput 
                            id="title" 
                            v-model="checkInForm.title"
                            size="sm" 
                            label="Reason for check-in"
                        />

                        <div v-if="currentClient.ccEmails.length" class="form__item mb--0">
                            <div class="checkbox__item auth--checkbox">
                                <input 
                                    id="termsAndService" 
                                    v-model="checkInForm.isCCEmails" 
                                    class="checkbox__input" 
                                    type="checkbox"
                                    @change="toggleAdditionalEmails"
                                >
                                <span class="checkbox--label p--0 text--xs" for="termsAndService">
                                    Use client corresponding emails
                                </span>
                            </div>
                        </div>

                        <div class="flex align-items-center justify-content-between mt--15">
                            <FormLabel title="What the client sees:" />
                            <BaseButton 
                                :label="!isSuggestion ? '+ Use suggestion' : '- Use custom text'" 
                                variant="ghost" 
                                variant-style="primary"
                                size="sm"
                                :disabled="isSuggestionDisabled"
                                @submit="isSuggestion = !isSuggestion"
                            />
                        </div>

                        <template v-if="isSuggestion && !isSuggestionDisabled">
                            <div class="rc__preview mt--5 grid gap-2">
                                <p class="rc__preview__text">
                                    To: <span class="ml--3">{{ currentClient.email }}</span>
                                </p>
    
                                <template v-if="checkInForm.isCCEmails">
                                    <div v-if="currentClient.ccEmails.length" class="flex flex-wrap rc__preview__text">
                                        <p>CC: </p>
                                        <p v-for="email in currentClient.ccEmails" :key="email" class="rc__preview__text ml--5">
                                            {{ email }},
                                        </p>
                                    </div>
                                </template>
    
                                <p class="rc__preview__text">
                                    Hi [Client's Name],
                                </p>

                                <template v-if="checkInForm.type.id === 'email'">
                                    <div>
                                        <p class="rc__preview__text">
                                            I hope this message finds you well. I just wanted to follow up on the introduction I sent earlier and see if you had a chance to review it
                                        </p>
                                        <p class="rc__preview__text">
                                            I’d be glad to answer any questions or provide more details if needed. Please let me know if now is a good time to connect, or if there's a better time that works for you.
                                        </p>
                                    </div>
                                </template>

                                <template v-else-if="checkInForm.type.id === 'service_provision'">
                                    <div>
                                        <p class="rc__preview__text">
                                            I hope you're doing well. I wanted to reach out and briefly introduce myself — I specialize in 
                                            <EmailTextSelect v-model="checkInForm.keywords.service_provision.specialization" placeholder="insert your skill or service" />, 
                                            and I help 
                                            <EmailTextSelect v-model="checkInForm.keywords.service_provision.help" placeholder="type of clients or businesses" /> 
                                            achieve 
                                            <EmailTextSelect v-model="checkInForm.keywords.service_provision.result" placeholder="specific result" />.
                                        </p>
                                        <p class="rc__preview__text">
                                            If you're currently working on 
                                            <EmailTextSelect v-model="checkInForm.keywords.service_provision.relevant_project" placeholder="relevant project or challenge" />, 
                                            I would love to explore how I can support you. Whether it is 
                                            <EmailTextSelect v-model="checkInForm.keywords.service_provision.keyOfferings" placeholder="insert benefit here" />. I bring 
                                            <EmailTextSelect v-model="checkInForm.keywords.service_provision.moreBenefits" placeholder="mention a strong point — experience, reliability, or a past result" />,.
                                        </p>

                                        <p class="rc__preview__text">
                                            Would you be open to a quick chat this week or next?
                                        </p>
                                    </div>
                                </template>

                                <template v-else-if="checkInForm.type.id === 'meeting'">
                                    <div>
                                        <p class="rc__preview__text">
                                            It was a pleasure speaking with you <EmailTextSelect v-model="checkInForm.keywords.meeting.time" placeholder="yesterday / earlier this week / at the event" /> — I enjoyed our conversation about <EmailTextSelect v-model="checkInForm.keywords.meeting.topic" placeholder="insert a specific topic you discussed if applicable" />.
                                        </p>
                                        <p class="rc__preview__text">
                                            As a quick follow-up, I wanted to reiterate my interest in <EmailTextSelect v-model="checkInForm.keywords.meeting.services" placeholder="collaborating / supporting your team / exploring opportunities together" />. Please let me know if you'd be open to continuing the conversation or if there’s a good time for a quick follow-up call.
                                        </p>
                                    </div>
                                </template>

                                <template v-else-if="checkInForm.type.id === 'call'">
                                    <div>
                                        <div class="rc__preview__text">
                                            Thank you again for taking the time to speak with me <EmailTextSelect v-model="checkInForm.keywords.call.time" placeholder="earlier today / yesterday" />. I appreciate the opportunity to learn more about <EmailTextSelect v-model="checkInForm.keywords.call.recap" placeholder="insert topic discussed earlier, e.g., an upcoming project, etc." />.
                                        </div> 
                                        <div class="rc__preview__text mt--5">
                                            As a quick recap, we discussed <EmailTextSelect v-model="checkInForm.keywords.call.topic" placeholder="insert key point or next step, if applicable" />. Please let me know if there’s anything else you need from my end, or if you'd like to schedule a follow-up to dive deeper.
                                        </div> 
                                    </div>
                                </template>

                                <template v-else />
    
                                <p class="rc__preview__text">
                                    Looking forward to hearing from you!
                                </p>

                                <p class="rc__preview__text">
                                    Best regards,
                                </p>

                                <p class="rc__preview__text">
                                    [Your Full Name]
                                </p>

                                <p class="rc__preview__text">
                                    [Your Email] | [Your Phone Number]
                                </p>

                                <EmailTextSelect v-model="checkInForm.keywords.footer.link" placeholder="LinkedIn or website, if relevant" />
                            </div>
                        </template>
                        
                        <template v-else>
                            <div class="form__textarea__group pb--15 pl--15">
                                <TipTap v-model="checkInForm.checkInNoteValue" class="pl--5" />
                            </div>
                        </template>
                    </div>
                </form>
            </template>
            <template #footer>
                <div class="w-100 flex justify-content-end align-items-center">
                    <BaseButton 
                        label="Cancel" 
                        variant="outline" 
                        class="mr--5"
                        :disabled="checkInLoading" 
                        @submit="closeModal"
                    />
                    <BaseButton 
                        label="Create event" 
                        :disabled="disableCheckInBtn" 
                        :loading="checkInLoading"
                        @submit="handleAddCheckIn"
                    />
                </div>
            </template>
        </MainModal>
    </div>
</template>

<script>
import MainModal from "./MainModal.vue";
import FormLabel from "../forms/FormLabel.vue";
import BaseButton from "../buttons/BaseButton.vue";
import FormInput from "../input/FormInput.vue";
import CustomDropdown from "../dropdowns/CustomDropdown.vue";
import TipTap from "../editor/TipTap.vue";
import { checkInTypes, timeRepeatSchedule } from "@/utils/dummy";
import EmailTextSelect from "@/components/clients/EmailTextSelect.vue";

export default {
    name: "CreateClientCheckIn",

    components: {
        MainModal,
        FormLabel,
        BaseButton,
        CustomDropdown,
        TipTap,
        EmailTextSelect,
        FormInput,
    },

    props: {
        actionType: { type: String, default: "add" },
        currentSelection: { type: String, default: "create" },
        showBillingInput: { type: Boolean, default: true },
        currentClient: { type: Object, default: () => {} },
        showModal: { type: Boolean, default: false },
    },

    data() {
        return {
            isSuggestion: true,
            checkInTypes,
            checkInForm: this.initialForm(),
            checkInFrequencies: Object.keys(timeRepeatSchedule),
            checkInLoading: false,
            isSuggestionDisabled: false,
        };
    },

    computed: {
        disableCheckInBtn() {
            if (!this.checkInForm.startDate || !this.checkInForm.type.id) return true;

            if ((!this.isSuggestion || this.isSuggestionDisabled) && !this.checkInNoteValue) return true;

            return false;
        },

        profile() {
            return this.$store.getters["auth/profile"];
        },
    },

    watch: {
        "checkInForm.type.id"(newVal) {
            if (newVal && newVal === "other") {
                this.isSuggestionDisabled = true;
            } else {
                this.isSuggestionDisabled = false;
            }
        },
    },

    methods: {
        setCheckInType() {
            this.$emit("set-type", this.checkInForm.type.id);
        },

        toggleAdditionalEmails(event) {
            const val = event.target.checked;

            if (val) {
                this.checkInForm.ccEmails = this.currentClient.ccEmails || [];
            } else {
                this.checkInForm.ccEmails = [];
            }
        },

        addEmail(email) {
            if (!email || this.checkInForm.ccEmails.includes(email)) return;

            this.checkInForm.ccEmails.push(email);
        },

        removeEmail(email) {
            this.checkInForm.ccEmails = this.checkInForm.ccEmails.filter(item => item !== email);
        },

        initialForm() {
            return {
                type: {
                    id: "",
                    name: "",
                },
                frequency: "",
                isCCEmails: false,
                startDate: null,
                ccEmails: [],
                checkInNoteValue: "",
                keywords: {
                    service_provision: {},
                    footer: {
                        link: "",
                    },
                    meeting: {},
                    call: {},
                    email: {},
                    reminder: {},
                },
                title: "",
            };
        },

        closeModal() {
            this.checkInForm = this.initialForm();
            this.$emit("close", false);
        },

        validateEmailTextFields() {            
            if (["other", "reminder"].includes(this.checkInForm.type.id) || this.checkInForm.checkInNoteValue) return true;

            const fieldsObj = this.checkInForm.keywords[this.checkInForm.type.id];
            
            let isValid = false;
            const fields = Object.values(fieldsObj);

            if (!fields.length) return false;
            
            for (let i = 0; i < fields.length; i++) {                
                if (!fields[i]) {
                    isValid = false;
                } else {                    
                    isValid = true;
                }
            }

            return isValid;
        },

        async handleAddCheckIn() {
            try {    
                const isValid = this.validateEmailTextFields();

                if (!isValid) {
                    this.$toast.error("Fill in all required details for email suggestion");
                    return;
                }

                this.checkInLoading = true;
                
                const payload = {   
                    subject: this.checkInForm.type.name,
                    checkInType: this.checkInForm.type.id,
                    startDate: this.checkInForm.startDate,
                    clientId: this.$route.params.id,
                    title: this.checkInForm.title,
                };

                if (!["other", "reminder"].includes(this.checkInForm.type.id)) {
                    payload.keywords = this.checkInForm.keywords;
                }

                await this.$store.dispatch("clients/addCheckIn", payload);

                this.$toast.success("Check-in event created");
                this.checkInForm = this.initialForm();
                this.$emit("close", false);
            } catch (error) {
                console.log({ error });
                const errMsg = error?.response?.data?.message || error.message;
                this.$toast.error(errMsg);
            } finally {
                this.checkInLoading = false;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.client__modal__body {    
    &.scroll-height {
        max-height: 490px;
        overflow: scroll;
        padding: 0 5px;
    }
}
</style>